"use client";

import { Close } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useUpdateAdsMutation } from "src/store/services/adminApi";
import { Loading } from "../ui";
import { adTypes } from "src/pages/ads/adsLanding";

interface EditAdsModalProps {
	isOpen: boolean;
	onClose: () => void;
	callback: () => void;
	adData: IAdvertisement;
}

const EditAdsModal: React.FC<EditAdsModalProps> = ({
	isOpen,
	onClose,
	callback,
	adData,
}) => {
	const [formData, setFormData] = useState<IAdvertisementUpdatePayload>({
		ad_id: adData._id,
		title: "",
		description: "",
		startDate: "",
		endDate: "",
		embedLink: "",
		subscriptionType: "",
		actionLabel: "",
		file: null,
		adType: "",
	});

	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	// RTK Query mutation
	const [updateAds, { isLoading }] = useUpdateAdsMutation();

	// Set initial form data from adData when the modal opens or adData changes
	useEffect(() => {
		if (isOpen && adData) {
			setFormData({
				ad_id: adData._id, // Required for update
				title: adData.title || "",
				description: adData.description || "",
				startDate: adData.startDate || "",
				endDate: adData.endDate || "",
				//@ts-ignore
				embedLink: adData.embedLink || "",
				subscriptionType: adData.subscriptionType || "",
				actionLabel: adData.actionLabel || "",
				file: null, // File will be updated if a new file is uploaded; keep null initially
				adType: adData.adType || "",
			});
			// Set preview URL if mediaUrl exists in adData
			if (adData.mediaUrl) {
				setPreviewUrl(adData.mediaUrl); // Use the existing media URL for preview
			}
		}
	}, [isOpen, adData]);

	// Clean up preview URL to prevent memory leaks
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	if (!isOpen) return null;

	const handleInputChange = (
		field: keyof IAdvertisementUpdatePayload,
		value: string | File | null
	) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const parseSize = (sizeString: string): { width: number; height: number } => {
		const [width, height] = sizeString.split(" x ").map(Number);
		return { width, height };
	};

	const checkImageDimensions = (
		width: number,
		height: number,
		expectedSize: string
	): boolean => {
		const { width: expectedWidth, height: expectedHeight } =
			parseSize(expectedSize);
		return width === expectedWidth && height === expectedHeight;
	};

	const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const file = e.target.files[0];
			const img = new Image();
			const url = URL.createObjectURL(file);
			img.onload = () => {
				const { width, height } = img;
				const type = adTypes.find((ad) => ad.slug === formData.adType);
				if (type && !checkImageDimensions(width, height, type.size)) {
					toast.error(
						`Image dimensions must be ${type.size}, got ${width}x${height}`
					);
					URL.revokeObjectURL(url);
					return;
				}
				handleInputChange("file", file);
				setPreviewUrl(url);
			};
			img.src = url;
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const file = e.dataTransfer.files[0];
			const img = new Image();
			const url = URL.createObjectURL(file);
			img.onload = () => {
				const { width, height } = img;
				const type = adTypes.find((ad) => ad.slug === formData.adType);
				if (type && !checkImageDimensions(width, height, type.size)) {
					toast.error(
						`Image dimensions must be ${type.size}, got ${width}x${height}`
					);
					URL.revokeObjectURL(url);
					return;
				}
				handleInputChange("file", file);
				setPreviewUrl(url);
			};
			img.src = url;
		}
	};

	const isValidHttpsUrl = (url: string): boolean => {
		if (!url) return true; // Empty link is valid (since it's optional)
		return url.startsWith("https://");
	};

	const handleSubmit = async () => {
		if (
			!formData.title ||
			!formData.startDate ||
			!formData.endDate ||
			!formData.description ||
			!formData.ad_id
		) {
			toast.error("Missing required fields");
			return;
		}

		// Validate HTTPS URL
		if (formData.embedLink && !isValidHttpsUrl(formData.embedLink)) {
			toast.error("Link must start with https://");
			return;
		}

		const submissionData = new FormData();
		submissionData.append("ad_id", formData.ad_id); // Required ad_id for update
		if (formData.title) submissionData.append("title", formData.title);
		if (formData.description)
			submissionData.append("description", formData.description);
		if (formData.startDate)
			submissionData.append("startDate", formData.startDate);
		if (formData.endDate) submissionData.append("endDate", formData.endDate);
		if (formData.embedLink)
			submissionData.append("embedLink", formData.embedLink);
		if (formData.subscriptionType)
			submissionData.append("subscriptionType", formData.subscriptionType);
		if (formData.actionLabel)
			submissionData.append("actionLabel", formData.actionLabel);
		if (formData.adType) submissionData.append("adType", formData.adType);
		if (formData.file) submissionData.append("file", formData.file);

		try {
			const result = await updateAds(submissionData).unwrap();
			toast.success(result?.message || "Advert updated successfully");
			setFormData({
				ad_id: "",
				title: "",
				description: "",
				startDate: "",
				endDate: "",
				embedLink: "",
				subscriptionType: "",
				actionLabel: "",
				file: null,
				adType: "",
			});
			callback();
			onClose();
		} catch (error: any) {
			const errorMessage =
				error?.data?.error?.message ||
				error?.message ||
				"Something went wrong while updating the advert";
			toast.error(errorMessage);
		}
	};

	const type = adTypes.find((ad) => ad.slug === formData.adType);

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
			<div className='bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-hidden'>
				<div className='flex justify-between items-center p-4 bg-gray-200 rounded-t-lg'>
					<h2 className='text-xl font-semibold text-gray-700'>Edit Advert</h2>
					<button
						onClick={onClose}
						className='text-gray-500 hover:text-gray-700 focus:outline-none'
						aria-label='Close modal'
					>
						<Close />
					</button>
				</div>
				<div className='space-y-6 p-4'>
					{/* Title */}
					<div>
						<label className='block text-sm font-medium text-gray-700'>
							Title
						</label>
						<input
							type='text'
							value={formData.title || ""}
							onChange={(e) => handleInputChange("title", e.target.value)}
							className='mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500'
							placeholder='Enter advert title'
							required
						/>
					</div>

					{/* Ads Type and Subscription Type */}
					<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
						<div>
							<label className='block text-sm font-medium text-gray-700'>
								Ads Type
							</label>
							<select
								value={formData.adType || ""}
								onChange={(e) => handleInputChange("adType", e.target.value)}
								className='mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500'
							>
								<option value='full'>Full Screen</option>
								<option value='carousel'>Slider</option>
								<option value='banner'>Pop up</option>
							</select>
						</div>
						<div>
							<label className='block text-sm font-medium text-gray-700'>
								Subscription Type
							</label>
							<select
								value={formData.subscriptionType || ""}
								onChange={(e) =>
									handleInputChange("subscriptionType", e.target.value)
								}
								className='mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500'
							>
								<option value=''>Select</option>
								<option value='regular'>Regular</option>
								<option value='premium'>Premium</option>
							</select>
						</div>
					</div>

					{/* Start and End Dates */}
					<div className='grid grid-cols-2 gap-4'>
						<div>
							<label className='block text-sm font-medium text-gray-700'>
								From
							</label>
							<input
								type='date'
								value={formData.startDate || ""}
								onChange={(e) => handleInputChange("startDate", e.target.value)}
								className='mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500'
								required
							/>
						</div>
						<div>
							<label className='block text-sm font-medium text-gray-700'>
								To
							</label>
							<input
								type='date'
								value={formData.endDate || ""}
								onChange={(e) => handleInputChange("endDate", e.target.value)}
								className='mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500'
								required
							/>
						</div>
					</div>

					{/* Description */}
					<div>
						<label className='block text-sm font-medium text-gray-700'>
							Description
						</label>
						<textarea
							value={formData.description || ""}
							onChange={(e) => handleInputChange("description", e.target.value)}
							className='mt-1 w-full p-2 border border-gray-300 rounded-lg h-24 resize-none focus:ring-purple-500 focus:border-purple-500'
							placeholder='Enter advert description'
							required
						/>
					</div>

					{/* Image Upload */}
					<div
						className='border border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400'
						onDragOver={handleDragOver}
						onDrop={handleDrop}
					>
						{previewUrl ? (
							<img
								src={previewUrl}
								alt='Preview'
								className='max-w-full max-h-40 object-contain mx-auto mb-4 rounded'
							/>
						) : (
							<div className='flex flex-col items-center'>
								<div className='w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-2'>
									<svg
										className='w-6 h-6 text-gray-400'
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'
										xmlns='http://www.w3.org/2000/svg'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
										/>
									</svg>
								</div>
								<p className='text-gray-500 text-sm mb-2'>
									or drag and drop your image here
								</p>
							</div>
						)}
						<input
							type='file'
							accept='image/*'
							onChange={handleImageUpload}
							className='hidden'
							id='imageUpload'
						/>
						<label
							htmlFor='imageUpload'
							className='inline-block px-4 py-2 bg-blue-400 text-white rounded-lg cursor-pointer hover:bg-blue-500'
						>
							Upload image
						</label>
						<h2 className='text-red-600 mt-2 text-sm'>
							Expected dimension: {type?.size}
						</h2>
					</div>

					{/* Embed Link (Optional) */}
					<div>
						<label className='block text-sm font-medium text-gray-700'>
							Embed Link (optional)
						</label>
						<input
							type='url'
							value={formData.embedLink || ""}
							onChange={(e) => {
								const value = e.target.value;
								handleInputChange("embedLink", value);
							}}
							onBlur={(e) => {
								const value = e.target.value;
								if (value && !isValidHttpsUrl(value)) {
									toast.error("Link must start with https://");
								}
							}}
							className={`mt-1 w-full p-2 border ${
								formData.embedLink && !isValidHttpsUrl(formData.embedLink)
									? "border-red-500"
									: "border-gray-300"
							} rounded-lg focus:ring-purple-500 focus:border-purple-500`}
							placeholder='Enter optional link (e.g., https://example.com)'
						/>
						<p className='text-xs text-gray-500 mt-1'>
							Link must start with https://
						</p>
					</div>

					{/* Action Label (Optional) */}
					<div>
						<label className='block text-sm font-medium text-gray-700'>
							Link Label
						</label>
						<input
							type='text'
							value={formData.actionLabel || ""}
							onChange={(e) => handleInputChange("actionLabel", e.target.value)}
							className='mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500'
							placeholder='e.g. Download now'
						/>
					</div>

					{/* Update Button */}
					<button
						onClick={handleSubmit}
						className='w-full p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center'
						disabled={
							!formData.title ||
							!formData.startDate ||
							!formData.endDate ||
							!formData.description ||
							isLoading
						}
					>
						{isLoading ? <Loading /> : "Update"}
					</button>
				</div>
			</div>
		</div>
	);
};

export default EditAdsModal;
